/* =============================================================================
   SELCA — theme.js  (Phase 5 complete)
   ============================================================================= */

(function () {
  'use strict';

  /* ---- Helpers -------------------------------------------------------------- */
  function qs(sel, root)  { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }
  function on(el, ev, fn) { if (el) el.addEventListener(ev, fn); }

  function debounce(fn, ms) {
    let t;
    return function (...args) { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), ms); };
  }

  function formatMoney(cents) {
    return '¥' + Math.round(cents / 100).toLocaleString('ja-JP');
  }

  /* =========================================================================
     DRAWER SYSTEM
     ========================================================================= */
  const overlay = qs('.js-drawer-overlay');

  function openDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay && overlay.classList.add('is-open');
    document.body.classList.add('drawer-is-open');
    // Move focus into drawer for a11y
    const firstFocusable = drawer.querySelector('button, a, input');
    if (firstFocusable) firstFocusable.focus();
  }

  function closeAllDrawers() {
    qsa('.drawer.is-open').forEach(d => {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    });
    overlay && overlay.classList.remove('is-open');
    document.body.classList.remove('drawer-is-open');
    qsa('[aria-expanded="true"]').forEach(b => b.setAttribute('aria-expanded', 'false'));
  }

  // Hamburger → left menu drawer
  on(qs('.js-hamburger'), 'click', function () {
    openDrawer(qs('#DrawerMenu'));
    this.setAttribute('aria-expanded', 'true');
  });

  // Cart icon → right cart drawer + refresh cart
  on(qs('.js-cart'), 'click', function (e) {
    e.preventDefault();
    openDrawer(qs('#CartDrawer'));
    this.setAttribute('aria-expanded', 'true');
    refreshCartDrawer();
  });

  // Delegate: close buttons + overlay
  on(document, 'click', function (e) {
    if (e.target.closest('.js-drawer-close') || e.target.closest('.js-drawer-overlay')) {
      closeAllDrawers();
    }
  });

  // Drawer search button → close drawer then focus header search
  on(document, 'click', function (e) {
    if (!e.target.closest('.js-drawer-search')) return;
    closeAllDrawers();
    setTimeout(function () {
      const searchInput = qs('.js-search-input');
      if (searchInput) {
        searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        searchInput.focus();
      }
    }, 320);
  });

  // Escape
  on(document, 'keydown', function (e) {
    if (e.key === 'Escape') closeAllDrawers();
  });

  /* =========================================================================
     DRAWER MENU — gender tabs + sub-nav accordion
     ========================================================================= */

  // Tab switcher
  on(document, 'click', function (e) {
    const tab = e.target.closest('.drawer-menu__tab');
    if (!tab) return;
    const drawer = tab.closest('#DrawerMenu');
    if (!drawer) return;

    // Deactivate all tabs + panels
    qsa('.drawer-menu__tab', drawer).forEach(t => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    qsa('.drawer-menu__panel', drawer).forEach(p => p.classList.remove('is-active'));

    // Activate clicked tab + its panel
    tab.classList.add('is-active');
    tab.setAttribute('aria-selected', 'true');
    const target = qs('#' + tab.getAttribute('aria-controls'), drawer);
    if (target) target.classList.add('is-active');
  });

  // Sub-nav accordion
  on(document, 'click', function (e) {
    const toggle = e.target.closest('.js-drawer-nav-toggle');
    if (!toggle) return;
    const targetId = toggle.getAttribute('data-target');
    const sub = qs('#' + targetId);
    if (!sub) return;
    const isOpen = sub.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(isOpen));
    // Rotate chevron via CSS if desired (add class)
    toggle.classList.toggle('is-expanded', isOpen);
  });

  /* =========================================================================
     CART AJAX
     ========================================================================= */

  const _cartDrawer = qs('#CartDrawer');
  const FREE_SHIPPING_THRESHOLD = _cartDrawer
    ? parseInt(_cartDrawer.dataset.freeShipping || '0', 10)
    : 0;

  // --- Fetch /cart.js and re-render the drawer ---
  async function refreshCartDrawer() {
    const itemsEl   = qs('#CartDrawerItems');
    const footerEl  = qs('#CartDrawerFooter');
    const shippingEl = qs('#CartDrawerShipping');

    if (itemsEl) itemsEl.innerHTML = '<div class="cart-drawer__loading">Loading…</div>';

    try {
      const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) throw new Error('cart fetch failed');
      const cart = await res.json();

      updateCartBadge(cart.item_count);
      if (itemsEl)   renderCartItems(itemsEl, cart);
      if (shippingEl) renderShippingBar(shippingEl, cart.total_price);
      if (footerEl)  renderCartFooter(footerEl, cart);
    } catch (err) {
      console.error('[SELCA] cart refresh error', err);
      if (itemsEl) itemsEl.innerHTML = '<div class="cart-drawer__loading">読み込みに失敗しました</div>';
    }
  }

  function renderCartItems(el, cart) {
    if (cart.item_count === 0) {
      el.innerHTML = `
        <div class="cart-drawer__empty">
          <p class="cart-drawer__empty-text">カートに商品がありません</p>
          <a href="/collections/all" class="cart-drawer__empty-link js-drawer-close">SHOP NOW →</a>
        </div>`;
      return;
    }

    el.innerHTML = cart.items.map(item => {
      const imgHtml = item.image
        ? `<img class="cart-drawer__item-img" src="${item.image}" alt="${escHtml(item.title)}" loading="lazy" width="100" height="150">`
        : `<div class="cart-drawer__item-img"></div>`;

      const variantHtml = (item.variant_title && item.variant_title !== 'Default Title')
        ? `<span class="cart-drawer__item-variant">${escHtml(item.variant_title)}</span>`
        : '';

      const vendorHtml = item.vendor
        ? `<span class="cart-drawer__item-vendor">${escHtml(item.vendor)}</span>`
        : '';

      return `
        <div class="cart-drawer__item" data-key="${item.key}">
          <a href="${item.url}">${imgHtml}</a>
          <div class="cart-drawer__item-info">
            ${vendorHtml}
            <a href="${item.url}" class="cart-drawer__item-title">${escHtml(item.product_title)}</a>
            ${variantHtml}
            <div class="cart-drawer__item-bottom">
              <span class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
              <div class="cart-drawer__qty">
                <button class="cart-drawer__qty-btn js-qty-minus"
                        aria-label="数量を減らす"
                        data-key="${item.key}"
                        data-qty="${item.quantity - 1}"
                        ${item.quantity <= 1 ? 'disabled' : ''}>&minus;</button>
                <span class="cart-drawer__qty-num">${item.quantity}</span>
                <button class="cart-drawer__qty-btn js-qty-plus"
                        aria-label="数量を増やす"
                        data-key="${item.key}"
                        data-qty="${item.quantity + 1}">&plus;</button>
              </div>
              <button class="cart-drawer__item-remove js-item-remove"
                      aria-label="${escHtml(item.title)}を削除"
                      data-key="${item.key}">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
                  <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>`;
    }).join('');
  }

  function renderShippingBar(el, totalCents) {
    if (totalCents >= FREE_SHIPPING_THRESHOLD) {
      el.innerHTML = `<p class="cart-drawer__shipping-done">✓ 送料無料が適用されています</p>`;
    } else {
      const remaining = FREE_SHIPPING_THRESHOLD - totalCents;
      const pct = Math.min(100, Math.round(totalCents / FREE_SHIPPING_THRESHOLD * 100));
      el.innerHTML = `
        <p class="cart-drawer__shipping-text">
          あと <strong>${formatMoney(remaining)}</strong> で送料無料
        </p>
        <div class="cart-drawer__progress-bar">
          <div class="cart-drawer__progress-fill" style="width:${pct}%;"></div>
        </div>`;
    }
  }

  function renderCartFooter(el, cart) {
    if (cart.item_count === 0) {
      el.style.display = 'none';
      return;
    }
    el.style.display = '';
    const subtotalEl = qs('#CartDrawerSubtotal', el);
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    } else {
      el.innerHTML = `
        <div class="cart-drawer__totals">
          <span class="cart-drawer__totals-label">合計</span>
          <span class="cart-drawer__totals-price" id="CartDrawerSubtotal">${formatMoney(cart.total_price)}</span>
        </div>
        <div class="cart-drawer__ctas">
          <a href="/checkout" class="cart-drawer__checkout">注文手続きへ</a>
          <a href="/cart" class="cart-drawer__continue js-drawer-close">ショッピングを続ける</a>

        </div>`;
    }
    // Update count label
    const countEl = qs('#CartDrawerCount');
    if (countEl) countEl.textContent = `(${cart.item_count})`;
  }

  // --- Quantity change (delegated) ---
  on(document, 'click', async function (e) {
    const btn = e.target.closest('.js-qty-minus, .js-qty-plus');
    if (!btn || !qs('#CartDrawer')) return;

    const key = btn.dataset.key;
    const qty = parseInt(btn.dataset.qty, 10);
    if (isNaN(qty) || qty < 0) return;

    btn.disabled = true;
    await cartChange(key, qty);
    await refreshCartDrawer();
  });

  // --- Remove item (delegated) ---
  on(document, 'click', async function (e) {
    const btn = e.target.closest('.js-item-remove');
    if (!btn) return;

    btn.disabled = true;
    await cartChange(btn.dataset.key, 0);
    await refreshCartDrawer();
  });

  async function cartChange(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error('cart change failed');
      return await res.json();
    } catch (err) {
      console.error('[SELCA] cart change error', err);
    }
  }

  /* =========================================================================
     ADD TO CART (product page form)
     ========================================================================= */
  on(document, 'submit', async function (e) {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();

    const submitBtn = qs('[type="submit"]', form);
    const originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = '追加中…';
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(new FormData(form))),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'add to cart failed');
      }

      // Open cart drawer & refresh
      openDrawer(qs('#CartDrawer'));
      await refreshCartDrawer();
    } catch (err) {
      console.error('[SELCA] add to cart error', err);
      alert(err.message || 'カートへの追加に失敗しました。');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  });

  /* =========================================================================
     CART BADGE
     ========================================================================= */
  function updateCartBadge(count) {
    const badge = qs('#CartBadge');
    if (!badge) return;
    badge.textContent = count;
    badge.classList.toggle('is-hidden', count === 0);
    const btn = qs('.js-cart');
    if (btn) btn.setAttribute('aria-label', `カートを開く（${count}点）`);
  }

  /* =========================================================================
     PREDICTIVE SEARCH
     ========================================================================= */
  const searchInput    = qs('.js-search-input');
  const searchDropdown = qs('.search-dropdown');

  if (searchInput) {
    on(searchInput, 'input', debounce(async function () {
      const q = this.value.trim();
      if (q.length < 2) {
        searchDropdown && searchDropdown.classList.remove('is-open');
        return;
      }
      try {
        const res = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`
        );
        if (!res.ok) return;
        const data = await res.json();
        renderSearchResults(data.resources.results.products || []);
      } catch (_) { /* silent */ }
    }, 220));

    on(document, 'click', function (e) {
      if (!e.target.closest('.site-header__search-form') && searchDropdown) {
        searchDropdown.classList.remove('is-open');
      }
    });
  }

  function renderSearchResults(products) {
    if (!searchDropdown) return;
    if (!products.length) { searchDropdown.classList.remove('is-open'); return; }

    searchDropdown.innerHTML = products.map(p => `
      <a class="search-dropdown__item" href="${p.url}">
        ${p.featured_image
          ? `<img class="search-dropdown__img" src="${p.featured_image}" alt="${escHtml(p.title)}" loading="lazy">`
          : ''}
        <div>
          <div class="search-dropdown__name">${escHtml(p.title)}</div>
          <div class="search-dropdown__price">${formatMoney(p.price)}</div>
        </div>
      </a>`).join('');
    searchDropdown.classList.add('is-open');
  }

  /* =========================================================================
     UTILITIES
     ========================================================================= */
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ---- Expose public API --------------------------------------------------- */
  window.SELCA_UI = {
    openDrawer,
    closeAllDrawers,
    updateCartBadge,
    refreshCartDrawer,
    formatMoney,
  };

})();
